/**
 * MKS-136 — the project's billing schedule on the project form, below the MKS-94 billing terms it depends on.
 *
 * Two shapes, decided by the project's billing frequency:
 *
 *   monthly / quarterly — the periods are GENERATED from the contract dates.
 *     The only action is "Generate periods", which is idempotent: pressing it
 *     again on an unchanged schedule changes nothing.
 *   per_phase — the PM defines the phases by hand, and a phase becomes ready to
 *     invoice only when they mark it done. The planned date is for forecasting.
 *
 * On a Fixed Price project the footer warns while the non-cancelled periods do
 * not add up to the contract amount. It is a warning, not a block, because a
 * schedule under construction legitimately does not add up yet — the backend
 * refuses the LAST period's mark-done until it does, which is the point where
 * the mismatch would actually bill the client the wrong total.
 *
 * `canEdit` (admin, finance, or this project's PM) only decides what is on
 * screen. The API re-checks every mutation; this is affordance, not the gate.
 */
import { useState } from 'react';
import { Button } from '../common/Button';
import {
  useBillingSchedule,
  useCancelBillingPeriod,
  useCreateBillingPeriod,
  useMarkBillingPeriodDone,
  useRegenerateBillingPeriods,
  useUpdateBillingPeriod,
} from '../../hooks/useBillingPeriods';
import {
  BILLING_PERIOD_STATUS_CLASSES,
  BILLING_PERIOD_STATUS_LABELS,
} from '../../services/api/billingPeriods';
import type { BillingPeriod } from '../../services/api/billingPeriods';
import { formatCurrency, formatDisplayDate } from '../../utils/format';
import { DateField } from '../ui/DateField';

interface BillingScheduleSectionProps {
  projectId: number;
  canEdit: boolean;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function BillingScheduleSection({ projectId, canEdit }: BillingScheduleSectionProps) {
  const { data, isLoading, error } = useBillingSchedule(projectId);
  const createMutation = useCreateBillingPeriod();
  const updateMutation = useUpdateBillingPeriod();
  const markDoneMutation = useMarkBillingPeriodDone();
  const cancelMutation = useCancelBillingPeriod();
  const regenerateMutation = useRegenerateBillingPeriods();

  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editAmount, setEditAmount] = useState('');

  const schedule = data?.data;
  const currency = schedule?.contractCurrency ?? 'CZK';

  // The endpoint 404s while the release flag is off, and this whole section is
  // additive — say nothing rather than showing an error for a feature the
  // reader has not been given yet.
  if (error) return null;
  if (isLoading || !schedule) return null;

  const editable = canEdit && schedule.canEdit;

  async function run(action: () => Promise<unknown>, successNotice?: string) {
    setActionError(null);
    setNotice(null);
    try {
      await action();
      if (successNotice) setNotice(successNotice);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Something went wrong');
    }
  }

  const handleAdd = () =>
    run(async () => {
      await createMutation.mutateAsync({
        projectId,
        input: {
          name: newName.trim(),
          plannedEndDate: newEnd,
          plannedAmount: newAmount === '' ? null : Number(newAmount),
        },
      });
      setAdding(false);
      setNewName('');
      setNewEnd('');
      setNewAmount('');
    });

  const startEditing = (period: BillingPeriod) => {
    setEditingId(period.id);
    setEditName(period.name);
    setEditEnd(period.plannedEndDate);
    setEditAmount(period.plannedAmount === null ? '' : String(period.plannedAmount));
    setActionError(null);
  };

  const handleSaveEdit = (periodId: number) =>
    run(async () => {
      await updateMutation.mutateAsync({
        projectId,
        periodId,
        input: {
          name: editName.trim(),
          plannedEndDate: editEnd,
          plannedAmount: editAmount === '' ? null : Number(editAmount),
        },
      });
      setEditingId(null);
    });

  const handleRegenerate = () =>
    run(async () => {
      const result = await regenerateMutation.mutateAsync({ projectId });
      const { created, updated, unchanged } = result.data;
      setNotice(
        created === 0 && updated === 0
          ? `The schedule is already up to date (${unchanged} periods).`
          : `${created} added, ${updated} updated.`,
      );
    });

  const sum = schedule.sum;
  const sumMismatch = schedule.fixedPrice && sum.contractAmount !== null && !sum.matches;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Billing schedule</h2>
          <p className="mt-1 text-sm text-gray-500">
            {schedule.recurring
              ? 'Generated from the billing frequency and the contract dates. An invoice goes out at the end of each period.'
              : 'The phases this project is billed in. A phase is ready to invoice only once you mark it done — the planned date is for forecasting.'}
            {!editable && ' Only admins, finance and the project manager can change it.'}
          </p>
        </div>
        {editable && schedule.recurring && (
          <Button
            type="button"
            variant="secondary"
            onClick={handleRegenerate}
            disabled={regenerateMutation.isPending}
          >
            {regenerateMutation.isPending ? 'Generating…' : 'Generate periods'}
          </Button>
        )}
        {editable && !schedule.recurring && !adding && (
          <Button type="button" variant="secondary" onClick={() => setAdding(true)}>
            Add phase
          </Button>
        )}
      </div>

      {actionError && (
        <p className="text-sm text-red-600" role="alert">
          {actionError}
        </p>
      )}
      {notice && <p className="text-sm text-gray-600">{notice}</p>}

      {schedule.periods.length === 0 && !adding && (
        <p className="text-sm text-gray-500">
          {schedule.recurring
            ? 'No periods yet. Press “Generate periods” once the contract dates are set.'
            : 'No phases yet.'}
        </p>
      )}

      {schedule.periods.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-3 font-medium">#</th>
                <th className="py-2 pr-3 font-medium">Name</th>
                <th className="py-2 pr-3 font-medium">Billed range</th>
                <th className="py-2 pr-3 font-medium text-right">Amount</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {schedule.periods.map((period) => {
                const isEditing = editingId === period.id;
                const frozen = period.status === 'invoiced' || period.status === 'cancelled';
                return (
                  <tr key={period.id} data-testid={`billing-period-${period.id}`}>
                    <td className="py-2 pr-3 text-gray-500">{period.sequence}</td>
                    <td className="py-2 pr-3">
                      {isEditing ? (
                        <input
                          type="text"
                          aria-label="Phase name"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="w-full rounded border border-gray-300 px-2 py-1"
                        />
                      ) : (
                        <span className="text-gray-900">{period.name}</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-gray-600 whitespace-nowrap">
                      {isEditing ? (
                        <DateField
                          aria-label="Planned end date"
                          value={editEnd}
                          onChange={setEditEnd}
                          className="rounded border border-gray-300 px-2 py-1"
                        />
                      ) : (
                        <>
                          {formatDisplayDate(period.billedFrom)} –{' '}
                          {formatDisplayDate(period.billedTo)}
                          {period.actualEndDate === null && period.type === 'phase' && (
                            <span className="ml-1 text-xs text-gray-400">(planned)</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      {isEditing ? (
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          aria-label="Planned amount"
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value)}
                          className="w-28 rounded border border-gray-300 px-2 py-1 text-right"
                        />
                      ) : period.plannedAmount === null ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        formatCurrency(period.plannedAmount, false, period.currency ?? currency)
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs ${BILLING_PERIOD_STATUS_CLASSES[period.status]}`}
                      >
                        {BILLING_PERIOD_STATUS_LABELS[period.status]}
                      </span>
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {editable && !frozen && (
                        <div className="flex justify-end gap-2">
                          {isEditing ? (
                            <>
                              <button
                                type="button"
                                className="text-sm text-primary-600 hover:underline"
                                onClick={() => handleSaveEdit(period.id)}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="text-sm text-gray-500 hover:underline"
                                onClick={() => setEditingId(null)}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              {period.type === 'phase' && period.status === 'planned' && (
                                <button
                                  type="button"
                                  className="text-sm text-primary-600 hover:underline"
                                  onClick={() =>
                                    run(() =>
                                      markDoneMutation.mutateAsync({
                                        projectId,
                                        periodId: period.id,
                                        actualEndDate: todayIso(),
                                      }),
                                    )
                                  }
                                >
                                  Mark phase done
                                </button>
                              )}
                              <button
                                type="button"
                                className="text-sm text-gray-600 hover:underline"
                                onClick={() => startEditing(period)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="text-sm text-gray-500 hover:underline"
                                onClick={() =>
                                  run(() =>
                                    cancelMutation.mutateAsync({
                                      projectId,
                                      periodId: period.id,
                                    }),
                                  )
                                }
                              >
                                Cancel period
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <div className="rounded-lg border border-dashed border-gray-300 p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label htmlFor="new-phase-name" className="block text-xs text-gray-600 mb-1">
                Phase name
              </label>
              <input
                id="new-phase-name"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label htmlFor="new-phase-end" className="block text-xs text-gray-600 mb-1">
                Planned end date
              </label>
              <DateField
                id="new-phase-end"
                value={newEnd}
                onChange={setNewEnd}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label htmlFor="new-phase-amount" className="block text-xs text-gray-600 mb-1">
                Planned amount ({currency})
              </label>
              <input
                id="new-phase-amount"
                type="number"
                min={0}
                step="0.01"
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleAdd}
              disabled={!newName.trim() || !newEnd || createMutation.isPending}
            >
              Add phase
            </Button>
            <Button type="button" variant="secondary" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {schedule.fixedPrice && (
        <div
          className={`rounded-lg px-3 py-2 text-sm ${
            sumMismatch ? 'bg-amber-50 text-amber-800' : 'bg-gray-50 text-gray-600'
          }`}
          data-testid="billing-schedule-sum"
        >
          {sum.contractAmount === null ? (
            <>
              Periods total {formatCurrency(sum.periodTotal, false, currency)}. Set the project’s
              contract amount to check it against the contract.
            </>
          ) : sumMismatch ? (
            <>
              The periods add up to {formatCurrency(sum.periodTotal, false, currency)}, but the
              contract is {formatCurrency(sum.contractAmount, false, currency)} —{' '}
              {(sum.difference ?? 0) > 0 ? 'over by ' : 'short by '}
              {formatCurrency(Math.abs(sum.difference ?? 0), false, currency)}. The last period
              cannot be marked ready until they match.
            </>
          ) : (
            <>
              The periods add up to the contract amount (
              {formatCurrency(sum.contractAmount, false, currency)}).
            </>
          )}
        </div>
      )}
    </div>
  );
}
